import React, { useMemo, useState } from "react";
import {
  Severity
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useAzureIndexesApi from "@/hooks/useAzureIndexesApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { AzureIndex } from "../types";
import IndexModal from "./IndexModal";
import DeleteIndexModal from "./DeleteIndexModal";
import IconEditPencil from "@/icons/IconEditPencil";
import IconTrash2 from "@/icons/IconTrash2";

type SortField = "name" | "createdAt" | "updatedAt" | "createdBy";
type SortDir = "asc" | "desc";

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <span className="ml-1 text-slate-300">↕</span>;
  return <span className="ml-1 text-slate-500">{sortDir === "asc" ? "↑" : "↓"}</span>;
}

export default function AINewIndexes() {
  const { azureIndexes, loading, add, update, remove } = useAzureIndexes();
  const { createIndex, editIndex, deleteIndex } = useAzureIndexesApi();
  const { subscription } = useSubscriptions();
  const toastAlert = useToastAlert();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<AzureIndex | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<AzureIndex | null>(null);
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleCreate = async (index: AzureIndex) => {
    const result = await createIndex({
      name: index.name,
      type: index.type,
      searchSize: index.searchSize,
      semanticSearchEnabled: index.semanticSearchEnabled
    });
    if (result.success && result.data) {
      add(result.data);
      toastAlert.displayToast(Severity.Success, "Index created successfully");
      setShowCreateModal(false);
    } else {
      toastAlert.displayToast(Severity.Error, result.error || "Failed to create index");
    }
  };

  const handleEdit = async (index: AzureIndex) => {
    const result = await editIndex({
      id: index.id,
      name: index.name,
      type: index.type,
      searchSize: index.searchSize,
      semanticSearchEnabled: index.semanticSearchEnabled
    });
    if (result.success && result.data) {
      update(result.data);
      toastAlert.displayToast(Severity.Success, "Index updated successfully");
      setEditingIndex(null);
    } else {
      toastAlert.displayToast(Severity.Error, result.error || "Failed to update index");
    }
  };

  const handleDelete = async (id: number) => {
    const result = await deleteIndex(id);
    if (result.success) {
      remove(id);
      toastAlert.displayToast(Severity.Success, "Index deleted successfully");
      setDeletingIndex(null);
    } else {
      toastAlert.displayToast(Severity.Error, result.error || "Failed to delete index");
    }
  };

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    return [...azureIndexes].sort((a, b) => {
      const av = ((a as any)[sortField] ?? "").toString().toLowerCase();
      const bv = ((b as any)[sortField] ?? "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [azureIndexes, sortField, sortDir]);

  const atLimit =
    subscription?.maxIndexCount !== undefined &&
    azureIndexes.length >= subscription.maxIndexCount;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Limit Warning */}
      {atLimit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-700">
            You have reached your index limit. Delete an unused index to create a new one.
          </p>
        </div>
      )}

      {/* When to use one vs multiple indexes */}
      <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 space-y-1.5">
        <p className="font-medium text-slate-800">One index or multiple?</p>
        <p>
          <strong>Use one index</strong> when your bots share the same knowledge domain — most businesses only need one. All your bots can query the same index simultaneously.
        </p>
        <p>
          <strong>Use separate indexes</strong> only when you have completely separate knowledge domains that must never cross (e.g. a public product bot and a private HR bot). Keep in mind: <strong>bots cannot query across indexes</strong> — if two bots ever need to compare or combine answers, they must share one index.
        </p>
      </div>

      {azureIndexes.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center">
          <div className="text-slate-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6M12 9v6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">No indexes yet</h3>
          <p className="text-sm text-slate-500 mb-4">
            Create your first knowledge index to enable AI-powered answers.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span className="text-base leading-none">+</span> Create Index
          </button>
        </div>
      ) : (
        <div className="bg-white border rounded-lg shadow-sm overflow-visible">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="text-sm text-slate-500">
              {azureIndexes.length} / {subscription?.maxIndexCount ?? "∞"} indexes
            </span>
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={atLimit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <span className="text-base leading-none">+</span> New Index
            </button>
          </div>
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b">
              <tr>
                {([
                  { field: "name" as SortField, label: "Name" },
                  { field: "createdAt" as SortField, label: "Created" },
                  { field: "updatedAt" as SortField, label: "Last Updated" },
                  { field: "createdBy" as SortField, label: "Created By" }
                ]).map(col => (
                  <th
                    key={col.field}
                    onClick={() => toggleSort(col.field)}
                    className="px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer select-none hover:text-slate-900 whitespace-nowrap"
                  >
                    {col.label}
                    <SortIcon field={col.field} sortField={sortField} sortDir={sortDir} />
                  </th>
                ))}
                <th className="px-4 py-3 text-sm font-medium text-slate-600 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((index) => (
                <tr key={index.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <button
                      className="font-medium text-sm text-primary hover:underline text-left"
                      onClick={() => setEditingIndex(index)}
                    >
                      {index.name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {index.createdAt ? new Date(index.createdAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {index.updatedAt ? new Date(index.updatedAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500 max-w-[140px] truncate">
                    {index.createdBy || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setEditingIndex(index)}
                        title="Edit"
                        className="p-1.5 rounded text-slate-400 hover:text-primary hover:bg-primary-bg transition-colors"
                      >
                        <IconEditPencil style={{ width: 16, height: 16 }} />
                      </button>
                      <button
                        onClick={() => setDeletingIndex(index)}
                        title="Delete"
                        className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <IconTrash2 style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* How it works */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 space-y-1.5">
        <p className="font-medium">How knowledge retrieval works</p>
        <p>
          Every document is searched two ways simultaneously: <strong>vector embeddings</strong> (catches intent and synonyms) and <strong>BM25 keyword matching</strong> (catches exact product codes and rare terms). Scores are combined — this hybrid approach is more accurate than either alone.
        </p>
        <p>
          <strong>Search Size</strong> controls how many passages are retrieved per question. Higher means more context for the AI. Recommended: 5–10.
        </p>
        <p>
          <strong>Semantic re-ranking</strong> runs a second neural pass to re-order results by meaning — it can improve answer quality on large indexes. It's off by default. If you'd like it enabled for your index, <a className="underline hover:text-blue-900" href="mailto:support@velaro.com">contact support</a>.
        </p>
        <p>
          Use <strong>Knowledge Overrides</strong> to pin exact answers to specific topics — they always win regardless of index scores.
        </p>
      </div>

      {showCreateModal && (
        <IndexModal onClose={() => setShowCreateModal(false)} onSave={handleCreate} />
      )}
      {editingIndex && (
        <IndexModal index={editingIndex} onClose={() => setEditingIndex(null)} onSave={handleEdit} />
      )}
      {deletingIndex && (
        <DeleteIndexModal index={deletingIndex} onClose={() => setDeletingIndex(null)} onDelete={handleDelete} />
      )}
    </div>
  );
}
